import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  BankAccount, BookingOption, PREMIUM_BENEFITS, TaxTreatment, formatEuro, payableCents,
  payableTaxLabel, taxTreatmentOf, vatNote,
} from '@radevents/shared';
import bookingService from '../../services/bookingService';
import BillingFields, {
  BILLING_CSS, BillingValue, billingComplete as isBillingComplete, billingRequest,
} from './BillingFields';
import BookingDone, { BOOKING_DONE_CSS } from './BookingDone';
import { trackOrganizerFunnel } from '../../services/trackingService';
import PremiumBenefitIcon from './PremiumBenefitIcon';
import { BERRY, FONT_HEAD, FONT_MONO, INK, LABEL, MUTED } from './organizerSpace';

interface Props {
  show: boolean;
  organizerId: number;
  /**
   * Für welchen Veranstalter gekauft wird.
   *
   * Steht am Preis, weil ein Konto mehrere Vereine verwalten kann (Agenturen tun das) — und
   * Premium gilt je Profil, nicht je Konto. Ohne den Namen kauft man am falschen.
   */
  organizerName?: string;
  /**
   * Der Aufrufer hat die Leistungen schon gezeigt — dann ist der Dialog nur noch die Bestellung.
   *
   * Die Verkaufskarte im Reiter *Sichtbarkeit & Promotion* nennt Nutzen, Beleg und Preis bereits. Wer
   * dort „Anfrage senden" drückt, hat entschieden; ihm dieselben vier Punkte noch einmal
   * vorzulegen, hält ihn auf und liest sich, als hätten wir seinen Klick nicht bemerkt.
   * Die Haken an anderen Stellen (Banner, Termin, Reiter) öffnen den Dialog weiterhin mit
   * vollem Kopf — dort ist er die Erklärung.
   */
  ordering?: boolean;
  /**
   * Vorschlag für Rechnungsempfänger und -anschrift aus den Stammdaten.
   *
   * Nur eine Vorbelegung: die Anschrift in `organizers` stammt größtenteils aus Importen und
   * dem Adress-Enricher und ist nicht vom Kunden bestätigt (siehe BillingFields).
   */
  billingDefault: BillingValue;
  onClose: () => void;
  /** Die Anfrage ist raus — der Aufrufer stellt den Pflege-Balken auf „angefragt" um. */
  onRequested: () => void;
}

/**
 * Die vier Symbole der Vorteilsliste. Bewusst hier und nicht in `shared`: die App zeichnet mit
 * `react-native-svg` und braucht dieselben Pfade in anderer Hülle — der *Name* ist geteilt
 * ([PremiumIcon]), die Zeichnung nicht.
 */
const CSS = `
.pd-backdrop { position:fixed; inset:0; z-index:60; display:flex; align-items:center;
  justify-content:center; padding:clamp(10px,3vw,34px); background:rgba(28,18,23,.66);
  backdrop-filter:blur(3px); }
.pd-sheet { width:100%; max-width:660px; max-height:calc(100vh - 34px); overflow:auto; background:#fff;
  border-radius:22px; box-shadow:0 40px 90px -34px rgba(20,12,16,.7); }
@media (prefers-reduced-motion: no-preference) {
  .pd-sheet { animation:pd-in .32s cubic-bezier(.2,.7,.3,1) both; }
  @keyframes pd-in { from { opacity:0; transform:translateY(9px); } to { opacity:1; transform:none; } }
}
.pd-head { position:relative; padding:30px clamp(22px,4vw,34px) 28px; color:#fff; overflow:hidden;
  background:linear-gradient(140deg,#2A171F 0%,#7E2549 58%,#B4587C 100%); }
/* Dasselbe Motiv wie im Seitenbanner — der Dialog soll aussehen, als komme er von dieser Seite. */
.pd-head::before { content:''; position:absolute; inset:0; opacity:.18;
  background:radial-gradient(circle at 88% 12%, #fff 0 44px, transparent 45px),
             radial-gradient(circle at 96% 74%, #fff 0 22px, transparent 23px); }
.pd-head > * { position:relative; }
.pd-close { position:absolute; top:14px; right:14px; width:34px; height:34px; border-radius:50%;
  display:flex; align-items:center; justify-content:center; cursor:pointer; color:#fff;
  border:1px solid rgba(255,255,255,.34); background:rgba(0,0,0,.22); z-index:2; }
.pd-close:hover { background:rgba(0,0,0,.36); }
.pd-close:focus-visible { outline:2px solid #fff; outline-offset:2px; }
.pd-eyebrow { font-family:${FONT_MONO}; font-size:11px; letter-spacing:.16em; text-transform:uppercase;
  color:rgba(255,255,255,.72); margin:0; }
.pd-title { font-family:${FONT_HEAD}; font-weight:800; font-size:clamp(25px,4.6vw,34px); line-height:1.12;
  letter-spacing:-.02em; margin:14px 0 0; max-width:26ch; }
.pd-lead { font-size:15px; line-height:1.6; color:rgba(255,255,255,.86); margin:15px 0 0; max-width:46ch; }

.pd-body { padding:24px clamp(22px,4vw,34px) 4px; }
.pd-item-title { font-family:${FONT_HEAD}; font-weight:800; font-size:15.5px; line-height:1.25;
  color:${INK}; margin:0; }
.pd-item-text { font-size:13.5px; color:#6A6A6A; margin:6px 0 0; line-height:1.55; }

/* Eine Spalte, nicht zwei: bei vier Abschnitten bleibt in der zweiten Reihe einer allein
   stehen, und die schmalere Spalte bricht jeden Titel zweizeilig um. Untereinander ist der
   Kasten sogar niedriger — der Knopf rueckt naeher, statt weiter weg. */
.pd-perks { display:grid; grid-template-columns:1fr; gap:15px; }
.pd-perk { display:flex; gap:12px; align-items:flex-start; }
.pd-perk-icon { flex:0 0 34px; width:34px; height:34px; border-radius:10px; display:flex;
  align-items:center; justify-content:center; background:rgba(146,44,87,.09); color:${BERRY}; }
/* Der erste Abschnitt über die volle Breite und umrandet: er ist der einzige Vorteil, den ein
   Verein nicht selbst herstellen kann. Der Kasten sagt das, ohne dass ein Satz es behaupten muss. */
.pd-perk--lead { padding:15px 16px; border:1px solid #EADCE3; border-radius:16px;
  background:rgba(146,44,87,.07); }
.pd-perk--lead .pd-perk-icon { background:#fff; }
.pd-perk--lead .pd-item-text { color:${INK}; }

.pd-billing { margin:22px clamp(22px,4vw,34px) 0; }
.pd-label { font-family:${FONT_MONO}; font-size:10.5px; letter-spacing:.12em; text-transform:uppercase;
  color:${BERRY}; margin:0 0 10px; }
${BILLING_CSS}
.pd-plans { display:grid; grid-template-columns:repeat(auto-fit,minmax(210px,1fr)); gap:12px;
  margin:10px clamp(22px,4vw,34px) 0; }
/* Wofuer und wie lange — eine Zeile ueber den Preisen. Sie ersetzt keinen Erklaerabsatz: die
   Laufzeit stand bisher nur klein am Preis („/ JAHR"), und der Verein ueberhaupt nirgends. */
.pd-for { margin:24px clamp(22px,4vw,34px) 0; font-size:13.5px; color:#6A6A6A; }
.pd-for strong { color:#2C2C2C; }
.pd-plan { text-align:left; cursor:pointer; font-family:inherit; background:#fff; border:1px solid #E5E1E1;
  border-radius:14px; padding:14px 16px; }
.pd-plan[aria-pressed="true"] { border:2px solid ${BERRY}; background:rgba(146,44,87,.05); }
.pd-plan:focus-visible { outline:2px solid ${BERRY}; outline-offset:2px; }
.pd-plan-kicker { font-family:${FONT_MONO}; font-size:10.5px; letter-spacing:.1em; text-transform:uppercase;
  color:${MUTED}; margin:0; }
.pd-plan[aria-pressed="true"] .pd-plan-kicker { color:${BERRY}; }
.pd-plan-price { font-family:${FONT_HEAD}; font-weight:800; font-size:25px; line-height:1; color:${INK};
  margin:9px 0 0; }
.pd-plan-price span { font-family:${FONT_MONO}; font-size:11px; font-weight:400; color:${MUTED};
  margin-left:7px; }
.pd-plan-vat { font-size:12px; color:${LABEL}; margin:5px 0 0; }
.pd-plan-text { font-size:12.5px; color:#6A6A6A; margin:7px 0 0; line-height:1.5; }
/* Der zu zahlende Betrag steht links vom Knopf, nicht darunter: die Zeile ist Teil der
   Entscheidung, nicht ihre Fussnote. Der Aussenabstand rechts haelt den Knopf aussen. */
.pd-total { font-size:13px; color:${LABEL}; margin:0 auto 0 0; }
.pd-total strong { color:${INK}; font-weight:700; }

.pd-foot { display:flex; flex-wrap:wrap; align-items:center; justify-content:flex-end; gap:12px 18px;
  padding:20px clamp(22px,4vw,34px) 26px; }
.pd-cta { font-family:inherit; font-size:14px; font-weight:700; border-radius:10px; padding:12px 20px;
  cursor:pointer; border:1px solid ${BERRY}; background:${BERRY}; color:#fff; white-space:nowrap;
  margin-left:auto; }
.pd-cta:hover:not(:disabled) { background:#7E2549; border-color:#7E2549; }
.pd-cta:disabled { opacity:.6; cursor:default; }
.pd-cta:focus-visible { outline:2px solid ${BERRY}; outline-offset:2px; }
/* Die Bestaetigung selbst steht in BookingDone (eigene Datei, eigenes CSS) — dieselbe Ansicht
   wie nach einer
   Hervorhebungs-Buchung. Zwei Nachbauten desselben Dankes liefen auseinander, sobald einer
   gepflegt wird, und beim Geld ist das die teuerste Stelle dafuer. */
/* Bestell-Zustand: derselbe Kopf, nur ohne Verkaufstext — die Karte davor hat ihn schon gesagt. */
.pd-title--order { font-size:24px; margin-bottom:2px; }
`;

/** Beschriftung einer Preisgruppe in der Sprache des Kunden, nicht der Datenbank. */
const AUDIENCE: Record<string, { kicker: string; text: string }> = {
  CLUB: { kicker: 'Verein', text: 'Eingetragene Vereine und gemeinnützige Veranstalter.' },
  COMMERCIAL: { kicker: 'Kommerzieller Anbieter', text: 'Agenturen, Reiseanbieter und gewerbliche Veranstalter.' },
  ANY: { kicker: 'Premium', text: 'Ein Jahr für dieses Veranstalter-Profil.' },
};

/**
 * Was Premium bringt — und der Weg, es anzufragen. Der eine Ort, an dem wir es erklären.
 *
 * Aufgerufen aus jedem Haken auf der Veranstalterseite: dem leeren Banner, dem Feld am Termin,
 * dem gesperrten Knopf „Bild & Flyer", dem Reiter *Zahlen*, der Karte in *Sichtbarkeit* und dem
 * Knopf im Pflege-Balken. **Alle öffnen denselben Dialog** — keine eigenen Unterseiten, keine
 * Varianten je Anlass. Der Grund ist derselbe wie beim Verzicht auf eine Werbeseite: es gibt
 * genau eine Erklärung, und sie steht dort, wo etwas fehlt.
 *
 * **Nur im Pflege-Zustand.** Besucher der Veranstalterseite bekommen ihn nicht zu sehen: für sie
 * wäre es Werbung für ein Angebot, das sie nicht kaufen können, und sie stünde zwischen den
 * Terminen des Vereins.
 *
 * **Die Preise kommen vom Server** (`/booking-options`), nicht aus dem Entwurf. Der Endpunkt
 * kennt die Einordnung des Veranstalters und liefert bei „nicht eingeordnet" beide Gruppen —
 * genau die Auswahl, die der Dialog zeigt. Fest verdrahtete 69/149 € wären beim ersten
 * Preisexperiment falsch, und zwar an der Stelle, an der jemand kauft.
 *
 * **Rechtliches steht im Text, nicht im Kleingedruckten**: Nettopreise mit der Steuerangabe am
 * Preis, der zu überweisende Bruttobetrag über dem Knopf, und dass mit dem Beantragen noch keine
 * Zahlungspflicht entsteht. Der Nettopreis ist das, was verglichen wird; der Bruttobetrag das,
 * was überwiesen wird, und deshalb steht er dort, wo bestellt wird.
 */
const PremiumDialog: React.FC<Props> = ({
  show, organizerId, organizerName, billingDefault, onClose, onRequested, ordering,
}) => {
  const [plans, setPlans] = useState<BookingOption[]>([]);
  const [chosen, setChosen] = useState<number | null>(null);
  const [billing, setBilling] = useState(billingDefault);
  const [saving, setSaving] = useState(false);
  /** Nach dem Absenden: Betrag, Verwendungszweck und Konto — die Bestätigung zeigt sie. */
  const [done, setDone] = useState<
    {
      reference: string; grossCents: number; vatPercent: number; taxTreatment?: TaxTreatment;
      bank?: BankAccount;
    } | null
  >(null);
  const sheet = useRef<HTMLDivElement>(null);
  /** Der Knopf, aus dem der Dialog kam — dorthin geht der Fokus zurück. */
  const opener = useRef<Element | null>(null);

  useEffect(() => {
    if (!show) return;
    trackOrganizerFunnel('premium_checkout_started', { offer: 'premium', organizerId });
    setDone(null);
    setBilling(billingDefault);
    bookingService.options(organizerId)
      .then(all => {
        const yearly = all.filter(o => o.kind === 'VERIFICATION' && o.available);
        setPlans(yearly);
        // Vorauswahl ist der günstigste: bei „nicht eingeordnet" stehen Vereins- und
        // Gewerbepreis nebeneinander, und die Selbstauskunft „Verein" ist der häufigere Fall.
        setChosen(yearly.length
          ? yearly.reduce((min, o) => (o.netCents < min.netCents ? o : min)).priceItemId
          : null);
      })
      .catch(() => setPlans([]));
    // billingDefault absichtlich nicht in den Abhängigkeiten: der Elternteil baut das Objekt bei
    // jedem Rendern neu, und der Effekt setzte dann das halb ausgefüllte Formular zurück.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, organizerId]);

  const close = useCallback(() => onClose(), [onClose]);

  // Fokus, Escape und Body-Scroll gehören zusammen: ein Dialog, aus dem die Tabulatortaste
  // hinausläuft, ist für Tastaturnutzer keiner.
  useEffect(() => {
    if (!show) return;
    opener.current = document.activeElement;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.body.classList.add('modal-open');
    sheet.current?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { close(); return; }
      if (e.key !== 'Tab' || !sheet.current) return;
      const focusable = sheet.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
      document.body.classList.remove('modal-open');
      (opener.current as HTMLElement | null)?.focus?.();
    };
  }, [show, close]);

  if (!show) return null;

  const selected = plans.find(p => p.priceItemId === chosen) ?? null;
  const billingComplete = isBillingComplete(billing);
  // Derselbe Steuerfall, den der Server gleich festschreibt (shared/src/tax.ts): aus der Schweiz
  // steht am Knopf der Nettobetrag „ohne MwSt.", nicht 19 % obendrauf.
  const treatment = taxTreatmentOf(billing.country, billing.vatId);

  const submit = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const created = await bookingService.create(organizerId, {
        kind: 'VERIFICATION',
        variant: selected.variant ?? undefined,
        priceItemId: selected.priceItemId,
        ...billingRequest(billing),
      });
      setDone({
        reference: created.paymentReference,
        grossCents: created.grossCents,
        vatPercent: created.vatPercent,
        taxTreatment: created.taxTreatment,
        bank: created.bank,
      });
      trackOrganizerFunnel('premium_requested', { offer: 'premium', organizerId });
      onRequested();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Die Anfrage konnte nicht abgeschickt werden');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="pd-backdrop" onClick={e => { if (e.target === e.currentTarget) close(); }}>
      <style>{CSS}{BOOKING_DONE_CSS}</style>
      <div
        className="pd-sheet"
        role="dialog"
        aria-modal="true"
        aria-label="Premium für Veranstalter"
        tabIndex={-1}
        ref={sheet}
      >
        {/* Der Verkaufskopf steht **nur vor** der Anfrage. Danach ist er beantwortet: die
            Überschrift wirbt für eine Entscheidung, die gerade gefallen ist, und über einer
            Bestätigung gelesen wirkt sie, als hätte der Klick nichts bewirkt. */}
        {!done && (
          <div className="pd-head">
            <button type="button" className="pd-close" onClick={close} aria-label="Schließen">
              <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor"
                strokeWidth="2.2" strokeLinecap="round" aria-hidden><path d="M6 6l12 12M18 6L6 18" /></svg>
            </button>
            <p className="pd-eyebrow">Premium</p>
            {ordering ? (
              <h2 className="pd-title pd-title--order">Premium anfragen</h2>
            ) : (<>
            {/* Gewinn, nicht Verlust. Die Überschrift hieß „Schade, wenn ihn jemand verpasst" —
                Angst vor dem Versäumnis ermüdet, und ein Verein, der sein Event seit Jahren
                stemmt, lässt sich davon nichts sagen. Sie spricht weiter von SEINER Arbeit, nur
                mit der Aussicht statt der Drohung. Der zweite Satz raeumt den groessten Einwand
                eines ehrenamtlichen Vorstands ab, bevor er entsteht: „noch mehr Aufwand" — und er
                stimmt, die Mails verschicken wir. */}
            <h2 className="pd-title">Interessierte automatisch informieren.</h2>
            <p className="pd-lead">
              Ihr steckt Monate Arbeit in einen Tag. Premium sorgt dafür, dass mehr Leute
              rechtzeitig davon erfahren — und zeigt euch, was davon ankommt.
            </p>
            </>)}
          </div>
        )}

        {done ? (
          <BookingDone
            next="schalten wir Premium frei"
            grossCents={done.grossCents}
            vatPercent={done.vatPercent}
            taxTreatment={done.taxTreatment}
            reference={done.reference}
            bank={done.bank}
            onClose={close}
          />
        ) : (
          <>
            {!ordering && <div className="pd-body">
              {/* Vier Abschnitte, einer je Sache — keine zweite Liste in zweiter Bauform daneben.
                  Der erste ist hervorgehoben, weil er der einzige Vorteil ist, den ein Verein
                  nicht selbst herstellen kann: ein Foto legt er auf seine eigene Website, an die
                  Leute, die seinen Termin bei uns vorgemerkt haben, kommt er nur über uns. */}
              <div className="pd-perks">
                {PREMIUM_BENEFITS.map(b => (
                  <div className={`pd-perk${b.lead ? ' pd-perk--lead' : ''}`} key={b.title}>
                    <span className="pd-perk-icon"><PremiumBenefitIcon name={b.icon} /></span>
                    <div style={{ minWidth: 0 }}>
                      <p className="pd-item-title">{b.title}</p>
                      <p className="pd-item-text">{b.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>}

            {plans.length > 0 && organizerName && (
              <p className="pd-for">Für <strong>{organizerName}</strong> · Laufzeit 1 Jahr</p>
            )}

            {plans.length > 0 && (
              <div className="pd-plans">
                {plans.map(p => {
                  const label = AUDIENCE[p.audience ?? 'ANY'] ?? AUDIENCE.ANY;
                  return (
                    <button
                      type="button"
                      key={p.priceItemId}
                      className="pd-plan"
                      aria-pressed={chosen === p.priceItemId}
                      onClick={() => setChosen(p.priceItemId)}
                    >
                      <p className="pd-plan-kicker">{label.kicker}</p>
                      <p className="pd-plan-price">
                        {formatEuro(p.netCents)}<span>/ JAHR</span>
                      </p>
                      {/* Die Steuerangabe gehört an den Preis und steht in eigener Zeile: in der
                          Mono-Versalie neben „/ JAHR" wurde aus „zzgl. MwSt." ein Wortbild, das
                          man liest, ohne es zu lesen. */}
                      <p className="pd-plan-vat">{vatNote(p.vatPercent)}</p>
                      {/* Die Leistungen stehen oben — sie hier zu wiederholen macht die Karte
                          hoch und sagt nichts Neues. Der Satz bleibt nur, wenn zwei Stufen zur
                          Wahl stehen: dann hilft er beim Zuordnen. */}
                      {plans.length > 1 && <p className="pd-plan-text">{label.text}</p>}
                    </button>
                  );
                })}
              </div>
            )}

            {plans.length > 0 && (
              <div className="pd-billing">
                <p className="pd-label">Rechnung</p>
                {/* Ohne Erklärsatz darunter: wozu eine Rechnungsanschrift da ist, weiß jeder,
                    und was nach dem Absenden passiert, steht in der Bestätigung. */}
                <BillingFields value={billing} onChange={setBilling} />
              </div>
            )}

            {/* Der Knopf und die Summenzeile daneben, sonst nichts. Die Steuerangabe steht am
                Preis, der Ablauf in der Bestätigung —
                ein Absatz, der beides noch einmal erzählt, wird nicht gelesen und schiebt den
                Knopf aus dem Bild. Kein zweiter Knopf: Abbrechen läuft über × oder den
                Hintergrund; ein gleichwertiges „Abbrechen" daneben macht aus einer Entscheidung
                eine Frage. */}
            <div className="pd-foot">
              {/* Der Bruttobetrag genau hier und sonst nirgends im Dialog: das Angebot rechnet
                  netto, überwiesen wird brutto, und diese Zahl steht gleich darauf in der
                  Bestätigung und auf dem Überweisungsträger. */}
              {selected && (
                <p className="pd-total">
                  Zu zahlen <strong>{formatEuro(payableCents(selected, treatment))}</strong>{' '}
                  {payableTaxLabel(treatment, selected.vatPercent)}
                </p>
              )}
              <button type="button" className="pd-cta" onClick={submit}
                disabled={!selected || !billingComplete || saving}
                title={selected && !billingComplete ? 'Bitte trag noch die Rechnungsanschrift ein.' : undefined}>
                {saving ? 'Wird gesendet…'
                  : selected ? `Premium beantragen · ${formatEuro(selected.netCents)}`
                    : 'Derzeit nicht buchbar'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PremiumDialog;
